import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserManager, User as OidcUser } from 'oidc-client-ts';
import { TenantConfiguration, SsoToken } from '../services/TenantService/types';
import { User } from '../services/types';
import { RequestPayload } from '../services/ApiClient/types';
import { tenantService } from '../services/'
import { AuthState, HybridAuthContextType } from './types';
import { AuthenticateResponse } from '../services/AccountService/types';

const HybridAuthContext = createContext<HybridAuthContextType | null>(null);

export const useHybridAuth = () => {
  const context = useContext(HybridAuthContext);
  if (!context) {
    throw new Error('useHybridAuth must be used within a HybridAuthProvider');
  }
  return context;
};

export const HybridAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [userManager, setUserManager] = useState<UserManager | null>(null);  
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        user: null,    
        authType: null
    });
    const [isProcessingSSO, setIsProcessingSSO] = useState(false);
    const [ssoPromise, setSsoPromise] = useState<Promise<User> | null>(null);
    const [authError, setAuthError] = useState<string | null>(null);


    // Function to sync SSO token with backend to get user data
    const handleSSOAuthentication = async (token: string): Promise<User> => {
        // If already processing, wait for the existing promise to resolve
        if (isProcessingSSO && ssoPromise) {
            console.log('SSO authentication already in progress, waiting for completion...');      
            try {
                return await ssoPromise;
            } catch (error) {
                console.log('Previous SSO attempt failed, trying again...');            
            }
        }

        // If we have an authenticated user already, return it
        if (authState.isAuthenticated && authState.user && authState.authType === 'sso') {
            console.log('SSO user already authenticated, returning existing data...');
            return authState.user;
        }
    
        setIsProcessingSSO(true);
        
        // Create and execute the SSO sync
        const executeSSO = async (): Promise<User> => {
            try {      
                console.log('SSO Token received:', {
                    tokenLength: token.length,
                    tokenType: typeof token,
                    hasToken: !!token
                });        

                // Basic JWT validation before sending to backend
                if (!token || typeof token !== 'string') {
                    throw new Error('Invalid token: token is null or not a string');
                }

                console.log('Calling sync-sso-token API...');
                
                let request: SsoToken = { token: token }
                let payload: RequestPayload<SsoToken> = { payload: request };
                const response = await tenantService.syncSsoToken(payload);
                    
                if (!response.success || !response.result) {
                    throw new Error('Failed to sync SSO token');
                }
                
                const jwtToken = response.result.jwtToken;
                localStorage.setItem('token', jwtToken);
                localStorage.setItem('authenticated', 'true');

                const userData: User = {
                    userName: response.result.username,
                    userPreferences: response.result.userPreferences.userPreferences,
                    userRestrictions: response.result.userPreferences.userRestrictions,
                    applicationFeatures: response.result.userPreferences.applicationFeatures,
                    courierId: response.result.courierId
                }
                
                localStorage.setItem('user', JSON.stringify(userData));                    
                return userData;                   
            } catch (error) {
                console.error('Error handling SSO authentication:', error);
                throw error;
            } finally {
                setIsProcessingSSO(false);
                setSsoPromise(null);           
            }
        };

        const promise = executeSSO();
        setSsoPromise(promise);
        return promise;
    };

    const createUserManager = (config: TenantConfiguration) => {
        return new UserManager({        
            authority: config.authority,
            client_id: config.clientId,
            redirect_uri: config.redirectUri,
            response_type: 'code',
            scope: 'openid profile email',
            post_logout_redirect_uri: window.location.origin,
            automaticSilentRenew: false, // handled through api-client
        });
    };

    // Handle traditional authentication
    const setTraditionalAuth = (user: User, token: string) => {
        localStorage.setItem("token", token);
        localStorage.setItem("authenticated", 'true');
        localStorage.setItem("user", JSON.stringify(user));

        setAuthState({
            isAuthenticated: true,
            user,
            authType: 'traditional'
        });
    };

    // SSO login with configuration
    const login = async (config?: TenantConfiguration) => {
        if (!config) {
            throw new Error('SSO configuration required');
        }

        try {
            const manager = createUserManager(config);
            setUserManager(manager);
            sessionStorage.setItem('tenantConfig', JSON.stringify(config));
            await manager.signinRedirect();
        } catch (error) {
            console.error('Login error:', error);

            // Clear all auth data
            localStorage.removeItem("token");
            localStorage.removeItem("authenticated");
            localStorage.removeItem("user");
            sessionStorage.removeItem('tenantConfig');
            throw error;
        }
    };

    const logout = async () => {
        try {
            // Clear all auth data
            localStorage.removeItem("token");
            localStorage.removeItem("authenticated");
            localStorage.removeItem("user");
            sessionStorage.removeItem('tenantConfig');

            if (authState.authType === 'sso' && userManager) {
                // For SSO, also redirect to identity provider logout
                await userManager.signoutRedirect();
            }

            setAuthState({
                isAuthenticated: false,
                user: null,
                authType: null
            });
        } catch (error) {
            console.error('Logout error:', error);
            
            // Still clear local state even if SSO logout fails
            setAuthState({
                isAuthenticated: false,
                user: null,
                authType: null
            });
        }
    };

    // Restore auth state on mount
    useEffect(() => {
        const restoreAuthState = async () => {
            // Check for existing authentication
            const token = localStorage.getItem("token");
            const storedUser = localStorage.getItem("user");
            
            if (token && storedUser) {
                setAuthState({
                    isAuthenticated: true,
                    user: JSON.parse(storedUser),
                    authType: 'traditional' // We can't easily distinguish, but it doesn't matter
                });
                return;
            }

            // Check for SSO callback
            const storedConfig = sessionStorage.getItem('tenantConfig');
            if (storedConfig) {
                const config = JSON.parse(storedConfig);
                const manager = createUserManager(config);
                setUserManager(manager);
            }
        };
        restoreAuthState();
    }, []);
  

    // Handle SSO auth state changes
    useEffect(() => {

        // console.log('HybridAuthContext - SSO useEffect:', { 
        //     hasUserManager: !!userManager, 
        //     currentPath: window.location.pathname,
        //     isProcessingSSO 
        // });

        if (!userManager) return;

        // Check if we're on the callback page and process it
        if (window.location.pathname.includes('/sso')) {
            console.log('HybridAuthContext - Processing SSO callback...');
            userManager.signinRedirectCallback()
                .then(user => {
                    console.log('HybridAuthContext - Callback processed successfully:', user);
                    // Clean up the URL
                    window.history.replaceState({}, document.title, '/');
                    // The UserLoaded event will be fired automatically
                })
                .catch(error => {
                    console.error('HybridAuthContext - Callback processing failed:', error);
                });
        } else {
            // Not on callback page, check for existing user
            userManager.getUser().then((user) => {
            if (user && !isProcessingSSO) {
                console.log('HybridAuthContext - Processing existing user from UserManager...');
                handleUserLoaded(user);
            }
            });
        }

        const handleUserLoaded = async (user: OidcUser): Promise<void> => {
            // prevent duplicae processing
            if (isProcessingSSO) {
                console.log('SSO user loading already in progress, skipping...')
                return;
            }
            
            try {        
                // Handle SSO authentication and get user data from your backend
                const userData = await handleSSOAuthentication(user.id_token);

                if (userData) {
                    setAuthState({
                        isAuthenticated: true,
                        user: userData,          
                        authType: 'sso'
                    });                
                }
                else {
                    throw new Error("SSO User data didn't sync correctly.");
                }
            } 
            catch (error) {
                console.error('Error handling SSO login:', error);    
                // Clear SSO data on error
                sessionStorage.removeItem('tenantConfig');
                setAuthState({
                    isAuthenticated: false,
                    user: null,
                    authType: null
                });
            }
        };

        userManager.getUser().then((user) => {
            if (user && !isProcessingSSO) {
                console.log('Processing intitial user from UserManager...');
                handleUserLoaded(user);
            }
        });

        // Subscribe to the UserLoaded event
        userManager.events.addUserLoaded(handleUserLoaded);

        const handleSilentRenewError = (error: Error) => {
            console.error('Silent renewal error', error);
            //ApiRequest should handle this        
            };

            userManager.events.addSilentRenewError(handleSilentRenewError);

            return () => {
                userManager.events.removeUserLoaded(handleUserLoaded);
                userManager.events.removeSilentRenewError(handleSilentRenewError);
            };
        }, [userManager]);

    const clearAuthError = () => setAuthError(null);

    const value = {
        isAuthenticated: authState.isAuthenticated,
        user: authState.user,
        authType: authState.authType,
        authError,
        login,
        logout,
        setTraditionalAuth,
        clearAuthError
    };

    return (
        <HybridAuthContext.Provider value={value}>
        {children}
        </HybridAuthContext.Provider>
    );
};